const CACHE_PREFIX = 'writer_admin_';
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Get cached data if it exists and is still valid (within TTL)
 */
export function getCachedData<T>(key: string): T | null {
  try {
    const fullKey = `${CACHE_PREFIX}${key}`;
    const cached = localStorage.getItem(fullKey);

    if (!cached) {
      return null;
    }

    const entry: CacheEntry<T> = JSON.parse(cached);
    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if cache is still valid
    if (age >= entry.ttl) {
      localStorage.removeItem(fullKey);
      return null;
    }

    return entry.data;
  } catch (error) {
    console.error('Failed to get cached data:', error);
    return null;
  }
}

/**
 * Set cached data with optional TTL (defaults to 5 minutes)
 */
export function setCachedData<T>(
  key: string,
  data: T,
  ttlMinutes?: number,
): void {
  try {
    const fullKey = `${CACHE_PREFIX}${key}`;
    const ttl = ttlMinutes ? ttlMinutes * 60 * 1000 : DEFAULT_TTL_MS;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
    };

    localStorage.setItem(fullKey, JSON.stringify(entry));
  } catch (error) {
    console.error('Failed to set cached data:', error);
    // If storage quota is exceeded, clear old cache entries
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      clearAllCache();
      try {
        setCachedData(key, data, ttlMinutes);
      } catch (retryError) {
        console.error('Failed to set cached data after clear:', retryError);
      }
    }
  }
}

/**
 * Invalidate (remove) a specific cache entry
 */
export function invalidateCache(key: string): void {
  try {
    const fullKey = `${CACHE_PREFIX}${key}`;
    localStorage.removeItem(fullKey);
  } catch (error) {
    console.error('Failed to invalidate cache:', error);
  }
}

/**
 * Clear all admin cache entries from localStorage
 */
export function clearAllCache(): void {
  try {
    const keysToRemove: string[] = [];

    // Collect all keys with the admin prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove all matching keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    console.log(`Cleared ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.error('Failed to clear all cache:', error);
  }
}
